import BigNumber from 'bignumber.js'
import { ethers } from 'ethers'
import { toastTypes } from 'toonswap-uikit'
import store from 'state'
import { push as pushToast } from 'state/toasts'

const GAS_LIMIT_MULTIPLIER = new BigNumber(2)
// Per-transaction gas cap (2^24) enforced by the RPC; a higher gas limit is rejected outright
const TX_GAS_LIMIT_CAP = new BigNumber(16777216)

/** The contract's revert reason from a failed gas estimate, e.g. "ERC20: burn amount exceeds balance". */
const getRevertReason = (error): string => {
  const message: string = error?.data?.message || error?.message || ''
  const match = message.match(/execution reverted:?\s*([^"\n]*)/i)
  if (match) {
    return match[1].trim() || 'the contract rejected it'
  }
  return message.split('\n')[0] || 'unknown error'
}

// Gas estimation fails when the call would revert. Sending it anyway lets the wallet fall back
// to a huge gas limit, which the RPC rejects ("gas limit too high") without the real reason,
// so stop here and show the contract's reason instead.
const withGasLimit = async (method, txConfig) => {
  let estimatedGas
  try {
    estimatedGas = await method.estimateGas(txConfig)
  } catch (error) {
    const reason = getRevertReason(error)
    store.dispatch(
      pushToast({
        id: 'transaction-would-fail',
        type: toastTypes.DANGER,
        title: 'Transaction would fail',
        description: reason,
      }),
    )
    throw new Error(`Transaction would fail: ${reason}`)
  }
  const gas = BigNumber.min(
    new BigNumber(estimatedGas).multipliedBy(GAS_LIMIT_MULTIPLIER).integerValue(BigNumber.ROUND_CEIL),
    TX_GAS_LIMIT_CAP,
  ).toString()
  return { ...txConfig, gas }
}

export const approve = async (lpContract, masterChefContract, account, gasBoostedPrice) => {
  const method = lpContract.methods.approve(masterChefContract.options.address, ethers.constants.MaxUint256)
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig)
}

export const approveVault = async (lpContract, archerswapVaultContract, account, gasBoostedPrice) => {
  const method = lpContract.methods.approve(archerswapVaultContract.options.address, ethers.constants.MaxUint256)
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig)
}

export const stake = async (masterChefContract, pid, amount, account, gasBoostedPrice, decimals = 18) => {
  // if (pid === 0) {
  //   return masterChefContract.methods
  //     .enterStaking(
  //       new BigNumber(amount).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  //     )
  //     .send({ from: account })
  //     .on('transactionHash', (tx) => {
  //       return tx.transactionHash
  //     })
  // }

  const method = masterChefContract.methods.deposit(
    pid,
    new BigNumber(amount).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  )
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const sousStake = async (sousChefContract, amount, decimals = 18, account, gasBoostedPrice) => {
  const method = sousChefContract.methods.deposit(
    new BigNumber(amount).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  )
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const sousStakeBnb = async (sousChefContract, amount, account, gasBoostedPrice) => {
  const method = sousChefContract.methods.deposit()
  const txConfig = await withGasLimit(method, {
    from: account,
    gasPrice: gasBoostedPrice,
    value: new BigNumber(amount).times(new BigNumber(10).pow(18)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const unstake = async (masterChefContract, pid, amount, account, gasBoostedPrice, decimals = 18) => {
  // if (pid === 0) {
  //   return masterChefContract.methods
  //     .leaveStaking(
  //       new BigNumber(amount).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  //     )
  //     .send({ from: account })
  //     .on('transactionHash', (tx) => {
  //       return tx.transactionHash
  //     })
  // }

  const method = masterChefContract.methods.withdraw(
    pid,
    new BigNumber(amount).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  )
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const sousUnstake = async (sousChefContract, amount, decimals = 18, account, gasBoostedPrice) => {
  // shit code: hard fix for old CTK and BLK
  if (sousChefContract.options.address === '0xFD919978845247e147364F0A1f1565AAC4Fcd472') {
    const method = sousChefContract.methods.emergencyWithdraw()
    const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
    return method.send(txConfig).on('transactionHash', (tx) => {
      return tx.transactionHash
    })
  }
  if (sousChefContract.options.address === '0xBb2B66a2c7C2fFFB06EA60BeaD69741b3f5BF831') {
    const method = sousChefContract.methods.emergencyWithdraw()
    const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
    return method.send(txConfig).on('transactionHash', (tx) => {
      return tx.transactionHash
    })
  }
  if (sousChefContract.options.address === '0x8825a44182b94641f9299C32EF44D21235563EF7') {
    const method = sousChefContract.methods.emergencyWithdraw()
    const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
    return method.send(txConfig).on('transactionHash', (tx) => {
      return tx.transactionHash
    })
  }

  const method = sousChefContract.methods.withdraw(
    new BigNumber(amount).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  )
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const sousEmegencyUnstake = async (sousChefContract, amount, account, gasBoostedPrice) => {
  const method = sousChefContract.methods.emergencyWithdraw()
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const harvest = async (masterChefContract, pid, account, gasBoostedPrice) => {
  // if (pid === 0) {
  //   return masterChefContract.methods
  //     .leaveStaking('0')
  //     .send({ from: account, gasPrice: gasBoostedPrice })
  //     .on('transactionHash', (tx) => {
  //       return tx.transactionHash
  //     })
  // }

  const method = masterChefContract.methods.deposit(pid, '0')
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const soushHarvest = async (sousChefContract, account, gasBoostedPrice) => {
  const method = sousChefContract.methods.deposit('0')
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const soushHarvestBnb = async (sousChefContract, account, gasBoostedPrice) => {
  const method = sousChefContract.methods.deposit()
  const txConfig = await withGasLimit(method, { from: account, value: new BigNumber(0), gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const harvestVault = async (archerswapStrategyContract, account, gasBoostedPrice) => {
  const method = archerswapStrategyContract.methods.harvest()
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const stakeVault = async (
  isCORE,
  archerswapVaultContract,
  amount,
  account,
  depositAll,
  gasBoostedPrice,
  decimals = 18,
) => {
  if (depositAll && !isCORE) {
    const method = archerswapVaultContract.methods.depositAll()
    const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
    return method.send(txConfig).on('transactionHash', (tx) => {
      return tx.transactionHash
    })
  }

  if (isCORE) {
    const method = archerswapVaultContract.methods.depositBNB()
    const txConfig = await withGasLimit(method, {
      from: account,
      value: new BigNumber(amount)
        .times(new BigNumber(10).pow(decimals))
        .integerValue(BigNumber.ROUND_FLOOR)
        .toString(),
      gasPrice: gasBoostedPrice,
    })
    return method.send(txConfig).on('transactionHash', (tx) => {
      return tx.transactionHash
    })
  }
  const method = archerswapVaultContract.methods.deposit(
    new BigNumber(amount).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  )
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const unstakeVault = async (
  isCORE,
  archerswapVaultContract,
  amount,
  account,
  withdrawAll,
  gasBoostedPrice,
  decimals = 18,
) => {
  if (withdrawAll && !isCORE) {
    const method = archerswapVaultContract.methods.withdrawAll()
    const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
    return method.send(txConfig).on('transactionHash', (tx) => {
      return tx.transactionHash
    })
  }

  if (withdrawAll && isCORE) {
    const method = archerswapVaultContract.methods.withdrawAllBNB()
    const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
    return method.send(txConfig).on('transactionHash', (tx) => {
      return tx.transactionHash
    })
  }

  const pricePerFullShareWei = await archerswapVaultContract.methods.getPricePerFullShare().call()
  const shares = new BigNumber(amount).times(new BigNumber(10).pow(decimals)).div(pricePerFullShareWei).toString()
  if (isCORE) {
    const method = archerswapVaultContract.methods.withdrawBNB(
      new BigNumber(shares).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
    )
    const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
    return method.send(txConfig).on('transactionHash', (tx) => {
      return tx.transactionHash
    })
  }

  const method = archerswapVaultContract.methods.withdraw(
    new BigNumber(shares).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  )
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const buyTickets = async (
  lotteryContract,
  lotteryId,
  ticketAmount,
  ticketPrice,
  ticketNumbers,
  account,
  gasBoostedPrice,
  lotteryType,
) => {
  const method = lotteryContract.methods.batchBuyLottoTicket(lotteryId, ticketAmount, ticketNumbers)
  const txConfig = await withGasLimit(method, {
    from: account,
    gasPrice: gasBoostedPrice,
    value: lotteryType === 'core' ? new BigNumber(ticketPrice).times(1e18).toString(10) : undefined,
  })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const claimReward = async (lotteryContract, lotteryId, ticketIds, account, gasBoostedPrice) => {
  const method = lotteryContract.methods.batchClaimRewards(lotteryId, ticketIds)
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const xbowStake = async (xbowContract, amount, account, gasBoostedPrice, decimals = 18) => {
  const method = xbowContract.methods.enter(
    new BigNumber(amount).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  )
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}

export const xbowUnstake = async (xbowContract, amount, account, gasBoostedPrice, decimals = 18) => {
  const method = xbowContract.methods.leave(
    new BigNumber(amount).times(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString(),
  )
  const txConfig = await withGasLimit(method, { from: account, gasPrice: gasBoostedPrice })
  return method.send(txConfig).on('transactionHash', (tx) => {
    return tx.transactionHash
  })
}
